<script setup lang="ts">
import { computed } from 'vue';
import { LimitedValue, LimitKey } from './utils/limitedValue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { I18nKey } from '@/utils/typings';
import { calculateBands, limitOrder, Band, calculateLimitsMinMax } from '@/utils/bandLimits';
import { toDisplayValue } from '@/utils/numberUtils';

const props = withDefaults(
  defineProps<{
    value: LimitedValue;
    paddingFraction?: number;
    unit?: string | null;
    disabled?: boolean;
  }>(),
  {
    paddingFraction: 0.1,
    unit: undefined,
    disabled: undefined,
  },
);

const limitRange = calculateLimitsMinMax([props.value], 10);
const bandInfo = computed(() => calculateBands(props.value, limitRange));

function toPercentage(absoluteValue: number) {
  const offset = -(bandInfo.value?.minLimit ?? 0);
  const range = bandInfo.value?.range ?? 0;

  const percentage = ((absoluteValue + offset) / range) * 100;
  return Math.max(0, Math.min(100, percentage));
}

function getWidth(band: Band) {
  return `${Math.abs(toPercentage(band.from) - toPercentage(band.to))}%`;
}

const limitsI18nKeys: Record<LimitKey, I18nKey> = {
  lowError: 'LowerFailedLimit',
  lowWarning: 'LowerWarningLimit',
  upWarning: 'UpperWarningLimit',
  upError: 'UpperFailedLimit',
};
</script>

<template>
  <Tooltip position="top">
    <template #target="{ props: activatorProps }">
      <div class="d-flex w-fill container" v-bind="activatorProps">
        <div class="marker" :style="{ left: `${toPercentage(value.value)}%` }">&#9660;</div>

        <div
          v-for="band in bandInfo?.bands"
          :key="band.title"
          class="band opacity-70 mt-2"
          :class="{ disabled }"
          :style="{ 'background-color': band.color, width: getWidth(band) }"
        />
      </div>
    </template>

    <template #tooltip>
      <div v-for="limitKey in limitOrder" :key="limitKey">
        <template v-if="value[limitKey] !== undefined">
          {{ $t(limitsI18nKeys[limitKey]) }}:
          {{
            toDisplayValue(value[limitKey], { signDisplay: 'exceptZero', unitAbbreviation: unit })
          }}
        </template>
      </div>
    </template>
  </Tooltip>
</template>

<style scoped lang="scss">
.container {
  position: relative;
}

.band {
  height: 10px;
  padding: 0;
  margin: 0;
}

.marker {
  position: absolute;
  z-index: 1;
  transform: translate(-0.5em, -0.25em);
  pointer-events: none;
}

.disabled {
  filter: grayscale(1);
}
</style>
